import React, { useState, useEffect, useRef } from 'react';
import { Switch, Route, useRouteMatch, useHistory, useLocation, Redirect } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import styles from './Profile.module.css';
import Footer from '../Footer';
import { Button } from '../Button';

function AccountSection() {
  const { instance, accounts } = useMsal();
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarTs, setAvatarTs] = useState(Date.now());
  const [avatarExists, setAvatarExists] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthToken = async () => {
    if (!accounts[0]) return null;
    const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return result.idToken;
  };

  useEffect(() => {
    if (!accounts[0]?.localAccountId) return;
    const userId = accounts[0].localAccountId;
    fetch('/api/profile-data?userId=' + userId)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setFirstName(data.firstName || '');
          setSecondName(data.secondName || '');
        }
      })
      .catch(() => {});

    fetch('/api/profile-avatar?userId=' + userId, { method: 'HEAD' })
      .then(r => { if (r.ok) { setAvatarExists(true); setAvatarTs(Date.now()); } })
      .catch(() => {});
  }, [accounts]);

  const saveName = async () => {
    setSaving(true);
    try {
      const token = await getAuthToken();
      await fetch('/api/profile-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ firstName, secondName }),
      });
      setEditingName(false);
    } catch (e) { /* no-op */ }
    setSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const token = await getAuthToken();
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await fetch('/api/profile-avatar', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) { setAvatarExists(true); setAvatarTs(Date.now()); }
    } catch (e) { /* no-op */ }
    setAvatarUploading(false);
  };

  const handleRemoveAvatar = async () => {
    try {
      const token = await getAuthToken();
      await fetch('/api/profile-avatar', { method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setAvatarExists(false);
    } catch (e) { /* no-op */ }
  };

  return (
    <div style={{ padding: 32 }}>
      {/* Avatar */}
      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Profile Picture:</strong><br />
          <div className={styles.profileAvatarWrapper}>
            {avatarExists
              ? <img src={`/api/profile-avatar?userId=${accounts[0]?.localAccountId}&t=${avatarTs}`} alt="Profile" className={styles.profileAvatar} />
              : <div className={styles.profileAvatarFallback}><i className="fas fa-user-circle"></i></div>
            }
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Button buttonStyle="btn--outline" onClick={() => fileInputRef.current.click()} disabled={avatarUploading}>
            {avatarUploading ? <><i className="fas fa-spinner fa-spin"></i> Uploading…</> : <><i className="fas fa-upload"></i> Upload Photo</>}
          </Button>
          {avatarExists && (
            <Button buttonStyle="btn--outline" style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }} onClick={handleRemoveAvatar}>
              <i className="fas fa-times"></i> Remove
            </Button>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
      </div>

      <hr className={styles.profileSeparator} />

      {/* Display Name */}
      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Display Name:</strong><br />
          {editingName ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input className={styles.profileInput} placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ maxWidth: 150 }} />
              <input className={styles.profileInput} placeholder="Last name" value={secondName} onChange={e => setSecondName(e.target.value)} style={{ maxWidth: 150 }} />
            </div>
          ) : (
            <span>{firstName || secondName ? `${firstName} ${secondName}`.trim() : <em style={{ color: '#666' }}>Not set</em>}</span>
          )}
        </div>
        {editingName ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button buttonStyle="btn--primary" onClick={saveName} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button buttonStyle="btn--outline" onClick={() => setEditingName(false)}>Cancel</Button>
          </div>
        ) : (
          <Button buttonStyle="btn--outline" onClick={() => setEditingName(true)}>Change</Button>
        )}
      </div>

      <hr className={styles.profileSeparator} />

      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Email:</strong><br />
          {accounts[0]?.username || accounts[0]?.name || '—'}
        </div>
      </div>

      <hr className={styles.profileSeparator} />

      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Close Account:</strong><br />
          Permanently delete all information &amp; subscriptions
        </div>
        <Button buttonStyle="btn--outline" style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }} onClick={() => alert('Delete account coming soon!')}>Delete</Button>
      </div>
    </div>
  );
}

function BillingSection() {
  return (
    <div style={{ padding: 32 }}>
      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Invoices:</strong><br />
          Download invoices for your past payments
        </div>
        <Button buttonStyle="btn--outline" onClick={() => alert('View invoices coming soon!')}>View</Button>
      </div>
      <hr className={styles.profileSeparator} />
      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Payment Methods:</strong><br />
          View and modify payment method
        </div>
        <Button buttonStyle="btn--outline" onClick={() => alert('Manage payment methods coming soon!')}>Manage</Button>
      </div>
    </div>
  );
}

function CloudSyncSection() {
  return <div style={{ padding: 32 }}>Cloud Sync page coming soon.</div>;
}

function DevicesSection() {
  return <div style={{ padding: 32 }}>Devices page coming soon.</div>;
}

function AppSettingsSection() {
  return (
    <div style={{ padding: 32 }}>
      <div className={styles.profileRow}>
        <div className={styles.profileField}>
          <strong>Default Purge:</strong><br />
          Change your application purge settings for your notes
        </div>
        <Button buttonStyle="btn--outline" onClick={() => alert('Change purge settings coming soon!')}>Change</Button>
      </div>
    </div>
  );
}

function DownloadSection() {
  return <div style={{ padding: 32 }}>Download page coming soon.</div>;
}

export default function Profile() {
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { instance, accounts } = useMsal();

  const getActive = (subPath) =>
    location.pathname === `${url}${subPath === '' ? '' : '/' + subPath}`;

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <>
      <div className={styles.profileContainer}>
        {/* Sidebar submenu */}
        <div className={styles.profileSidebar}>
          <Button
            buttonStyle={getActive('account') || location.pathname === url ? 'btn--primary' : 'btn--outline'}
            className="profile-submenu-btn"
            onClick={() => history.push(`${url}/account`)}
          >
            Account
          </Button>
          <Button
            buttonStyle={getActive('billing') ? 'btn--primary' : 'btn--outline'}
            className="profile-submenu-btn"
            onClick={() => history.push(`${url}/billing`)}
          >
            Billing
          </Button>
          <Button
            buttonStyle={getActive('cloudsync') ? 'btn--primary' : 'btn--outline'}
            className="profile-submenu-btn"
            onClick={() => history.push(`${url}/cloudsync`)}
          >
            Cloud Sync
          </Button>
          <Button
            buttonStyle={getActive('devices') ? 'btn--primary' : 'btn--outline'}
            className="profile-submenu-btn"
            onClick={() => history.push(`${url}/devices`)}
          >
            Devices
          </Button>
          <Button
            buttonStyle={getActive('appsettings') ? 'btn--primary' : 'btn--outline'}
            className="profile-submenu-btn"
            onClick={() => history.push(`${url}/appsettings`)}
          >
            App Settings
          </Button>
          <Button
            buttonStyle={getActive('download') ? 'btn--primary' : 'btn--outline'}
            className="profile-submenu-btn"
            onClick={() => history.push(`${url}/download`)}
          >
            Download
          </Button>
          <Button
            buttonStyle="btn--primary"
            className="profile-submenu-btn logout-btn"
            onClick={handleLogout}
          >
            Log Out
          </Button>
        </div>
        {/* Main content */}
        <div className={styles.profileMain}>
          <Switch>
            <Route exact path={path}>
              {/* Redirect /profile to /profile/account */}
              <Redirect to={`${url}/account`} />
            </Route>
            <Route path={`${path}/account`}>
              <AccountSection />
            </Route>
            <Route path={`${path}/billing`}>
              <BillingSection />
            </Route>
            <Route path={`${path}/cloudsync`}>
              <CloudSyncSection />
            </Route>
            <Route path={`${path}/devices`}>
              <DevicesSection />
            </Route>
            <Route path={`${path}/appsettings`}>
              <AppSettingsSection />
            </Route>
            <Route path={`${path}/download`}>
              <DownloadSection />
            </Route>
          </Switch>
        </div>
      </div>
      <Footer />
    </>
  );
}